import {
  DishAllergen,
  DishIngredient,
  DishResponse,
  GetAllResolvedEdiblesResponse,
} from "api-types";

import React, { useMemo } from "react";

import { Box, Stack, Typography, useTheme } from "@mui/material";

import { NotInterestedRounded } from "@mui/icons-material";

import { useTranslation } from "../../../i18n";
import { DynamicDrawer } from "../../Drawer/DynamicDrawer";
import CloseIcon from "../../Icons/Close";
import { MappedProduct } from "../ProductDrawer/product.types";
import { renderAllergenDetails } from "./renderAllergenDetails";
import { renderIngredientDetails } from "./renderIngredientDetails";

interface RelatedIngredientsDrawerProps {
  dish: DishResponse;
  linkedItems?: GetAllResolvedEdiblesResponse;
  matchedAllergens: DishAllergen[];
  matchedIngredients: DishIngredient[];
  open: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
  onProductClick?: (product: MappedProduct) => void;
  onClose?: () => void;
}

/**
 * Converts a RichIngredient into a full DishIngredient
 */
const toDishIngredient = (
  i: any
): DishIngredient & { allergens?: DishAllergen[] } => ({
  ...i,
  ingredientName: i.ingredientName ?? i.name,
  igfdTaxAllergenIds: i.igfdTaxAllergenIds ?? [],
  igfdTaxNotSuitableForIds: i.igfdTaxNotSuitableForIds ?? [],
  convertValues: i.convertValues ?? {},
});

export const RelatedIngredientsDrawer = ({
  dish,
  open,
  linkedItems,
  onClose,
  onProductClick,
  matchedAllergens,
  matchedIngredients,
  containerRef,
}: RelatedIngredientsDrawerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { oscar } = theme.palette;

  const linkedDish = linkedItems?.dishes[dish.id];
  const optionalProductIds = dish.optionalProductIds;
  const optionalIngredientIds = linkedDish?.optionalIngredientIds;

  const filteredProducts = useMemo(() => {
    return linkedDish?.directlyLinkedProducts
      .filter((productRef) => {
        const product = linkedItems?.products[productRef.id];
        return matchedAllergens?.some(({ igfdTaxAllergenId }) =>
          product?.allAllergens.some(
            (allergen) => allergen.igfdTaxAllergenId === igfdTaxAllergenId
          )
        );
      })
      .map((productRef) => {
        const product = linkedItems?.products[productRef.id];
        const allergens = product?.allAllergens.filter((allergen) =>
          matchedAllergens?.some(
            (m) => m.igfdTaxAllergenId === allergen.igfdTaxAllergenId
          )
        );
        return {
          ...productRef,
          allergens,
          isOptional: optionalProductIds?.includes(productRef.id),
          ingredients: (product?.allIngredients || []).map(toDishIngredient),
          onClick: onProductClick,
          name: product?.name || productRef.name,
        };
      });
  }, [
    linkedDish,
    linkedItems,
    matchedAllergens,
    optionalProductIds,
    onProductClick,
  ]);

  return (
    <DynamicDrawer
      ref={containerRef}
      drawerType="small"
      open={open}
      onClose={onClose}
      Header={
        <Stack alignItems="center" justifyItems="flex-start" gap={1}>
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <NotInterestedRounded
              fontSize="small"
              sx={{
                color: oscar.light.icon.danger,
                fontSize: 20,
              }}
            />
          </Stack>
          <Typography
            color={oscar.light.icon.danger}
            variant="headingRLight"
            textAlign="center"
          >
            {t("allergenDetail.title")}
          </Typography>
        </Stack>
      }
      Footer={
        <CloseIcon
          color={oscar.light.icon.black}
          style={{ cursor: "pointer" }}
          height={32}
          width={32}
          onClick={onClose}
        />
      }
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        gap={3}
        marginTop={4}
        width="100%"
      >
        {renderAllergenDetails({
          matchedAllergens,
          linkedDish,
          optionalIngredientIds,
          filteredProducts: filteredProducts || [],
          onProductClick,
        })}
        <Box
          sx={{
            borderTop: `1px solid ${oscar.light.borders}`,
            width: "100%",
            pt: 2,
          }}
        >
          {renderIngredientDetails({
            matchedIngredients,
            linkedDish,
            linkedItems,
            optionalProductIds,
            optionalIngredientIds,
          })}
        </Box>
      </Box>
    </DynamicDrawer>
  );
};
